import React from "react";
import { Link } from "react-router-dom";
import { Phone, Printer, Mail, Facebook, Instagram, Linkedin, Twitter, Youtube } from "lucide-react";

export const ADN_CONTACT = {
  phone: "(555) 555-0100",
  fax: "(555) 555-0101",
  email: "contact@adnmarketplace.com",
  facebook: "#",
  instagram: "#",
  linkedin: "#",
  twitter: "#",
  youtube: "#",
};

export default function Footer() {
  return (
    <footer className="bg-[var(--adn-navy)] text-white/80 grain">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-[var(--adn-gold)] flex items-center justify-center text-[var(--adn-navy)] font-bold font-display">A</div>
            <span className="font-display font-semibold text-white text-lg">ADN Marketplace</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-white/65 max-w-sm">
            A neutral marketplace connecting distressed property owners with verified buyers, agents, lenders, attorneys, and contractors.
          </p>

          <ul className="mt-6 space-y-2 text-sm">
            <li className="flex items-center gap-2.5 text-white/80">
              <Phone size={14} className="text-[var(--adn-gold)]" />
              <a href={`tel:${ADN_CONTACT.phone.replace(/\D/g, "")}`} data-testid="footer-phone" className="hover:text-white">{ADN_CONTACT.phone}</a>
            </li>
            <li className="flex items-center gap-2.5 text-white/80">
              <Printer size={14} className="text-[var(--adn-gold)]" />
              <span data-testid="footer-fax">Fax: {ADN_CONTACT.fax}</span>
            </li>
            <li className="flex items-center gap-2.5 text-white/80">
              <Mail size={14} className="text-[var(--adn-gold)]" />
              <a href={`mailto:${ADN_CONTACT.email}`} data-testid="footer-email" className="hover:text-white">{ADN_CONTACT.email}</a>
            </li>
          </ul>

          <div className="mt-5 flex items-center gap-2">
            {[
              { icon: Facebook, href: ADN_CONTACT.facebook, label: "Facebook", t: "facebook" },
              { icon: Instagram, href: ADN_CONTACT.instagram, label: "Instagram", t: "instagram" },
              { icon: Linkedin, href: ADN_CONTACT.linkedin, label: "LinkedIn", t: "linkedin" },
              { icon: Twitter, href: ADN_CONTACT.twitter, label: "X / Twitter", t: "twitter" },
              { icon: Youtube, href: ADN_CONTACT.youtube, label: "YouTube", t: "youtube" },
            ].map(({ icon: Icon, href, label, t }) => (
              <a
                key={t}
                href={href}
                aria-label={label}
                data-testid={`footer-social-${t}`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 inline-flex items-center justify-center border border-white/15 text-white/75 hover:text-[var(--adn-navy)] hover:bg-[var(--adn-gold)] hover:border-[var(--adn-gold)] transition-colors"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>
        <div>
          <p className="eyebrow mb-3">Sellers</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/for-sellers" className="hover:text-white">Property Help</Link></li>
            <li><Link to="/submit-property" className="hover:text-white">Submit Property</Link></li>
            <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
            <li><Link to="/download" className="hover:text-white">Get the App</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-3">Providers</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/for-investors" className="hover:text-white">For Investors</Link></li>
            <li><Link to="/for-professionals" className="hover:text-white">Realtors / Attorneys / Lenders</Link></li>
            <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-3">Legal</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
            <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
            <li><Link to="/disclaimer" className="hover:text-white">Disclaimer</Link></li>
          </ul>
        </div>
      </div>

      <div className="gold-rule" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 text-xs text-white/55 flex flex-col md:flex-row justify-between gap-3">
        <p>© {new Date().getFullYear()} ADN Distressed Property Marketplace™. All rights reserved.</p>
        <p className="max-w-3xl">
          ADN does not provide legal, tax, financial, or real estate advice. Offers are submitted by independent third-party providers.
        </p>
      </div>
    </footer>
  );
}
